import { useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { token } from "@atlaskit/tokens";

import { Button } from "@/libraries/design/components/button/src/button.tsx";
import { Link } from "@/libraries/design/components/link/src/link.tsx";
import { Modal } from "@/libraries/design/components/modal/src/modal.tsx";
import { wacToken } from "@/libraries/design/foundations/index.ts";

import {
    useClearSelectedFeature,
    useSelectedFeatureData
} from "../context/hooks.ts";

import { FeatureDescription } from "./feature-description.tsx";
import { StatusLozenge } from "./status-lozenge.tsx";

export const SelectedFeatureModal = () => {
    const selectedFeature = useSelectedFeatureData();
    const clearSelectedFeature = useClearSelectedFeature();
    const [isCopied, setIsCopied] = useState(false);

    const intl = useIntl();

    const header = (
        <div>
            <span
                css={css({
                    margin: 0,
                    fontSize: "0.9rem",
                    fontWeight: 900,
                    textTransform: "uppercase"
                })}
            >
                {selectedFeature?.category.roadmapCategoryNameExternal}
            </span>
            <h4
                css={css({
                    fontSize: "1.5rem",
                    fontWeight: 500,
                    marginBlockEnd: 0,
                    marginBlockStart: token("space.200")
                })}
            >
                {selectedFeature?.title}
            </h4>
        </div>
    );

    return (
        <Modal
            isOpen={!!selectedFeature}
            closeModal={clearSelectedFeature}
            title={header}
            width="medium"
            closeLabel={intl.formatMessage({
                id: "SelectedFeatureModal.close",
                defaultMessage: "Close"
            })}
        >
            {selectedFeature && (
                <>
                    <FeatureDescription content={selectedFeature.description} />

                    {selectedFeature.relatedLinksCollection?.items &&
                        selectedFeature.relatedLinksCollection.items.length >
                            0 && (
                            <div
                                css={css({
                                    marginBlockStart: token("space.200"),
                                    marginBlockEnd: token("space.200")
                                })}
                            >
                                <ul
                                    css={css({
                                        listStyle: "none",
                                        padding: 0,
                                        margin: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: token("space.100")
                                    })}
                                >
                                    {selectedFeature.relatedLinksCollection.items.map(
                                        (link, index) => (
                                            <li key={index}>
                                                <Link
                                                    text={link.ctaButtonText}
                                                    href={link.ctaUrl}
                                                    type="primary"
                                                    aria-label={
                                                        link.ctaButtonText
                                                    }
                                                />
                                            </li>
                                        )
                                    )}
                                </ul>
                            </div>
                        )}

                    <div
                        css={css({
                            display: "flex",
                            flexDirection: "column",
                            gap: token("space.200")
                        })}
                    >
                        <div>
                            {intl.formatMessage({
                                id: "SelectedFeatureModal.share",
                                defaultMessage: "Share this update:"
                            })}
                            <div
                                css={css({
                                    display: "flex",
                                    gap: token("space.200")
                                })}
                            >
                                <input
                                    readOnly={true}
                                    value={getShareableUrl()}
                                    css={css({
                                        flex: 1,
                                        minWidth: 0,
                                        height: token("space.400"),
                                        border: `2px solid ${wacToken(
                                            "color.neutral.light400"
                                        )}`,
                                        borderRadius:
                                            token("border.radius.200"),
                                        padding: `0 ${token("space.100")}`,
                                        position: "relative",
                                        insetBlockStart: `-${token(
                                            "space.100"
                                        )}`
                                    })}
                                />
                                <Button
                                    onClick={() => {
                                        navigator.clipboard.writeText(
                                            getShareableUrl()
                                        );
                                        setIsCopied(true);
                                        setTimeout(() => {
                                            setIsCopied(false);
                                        }, 2000);
                                    }}
                                >
                                    {isCopied
                                        ? intl.formatMessage({
                                              id: "SelectedFeatureModal.linkCopied",
                                              defaultMessage: "Link Copied"
                                          })
                                        : intl.formatMessage({
                                              id: "SelectedFeatureModal.copyLink",
                                              defaultMessage: "Copy link"
                                          })}
                                </Button>
                            </div>
                        </div>

                        <div css={css({ display: "flex", gap: 4 })}>
                            <StatusLozenge
                                text={
                                    selectedFeature.status
                                        .roadmapStatusNameExternal
                                }
                            />
                            <span>{selectedFeature.timeFrame}</span>
                        </div>

                        <div>
                            {intl.formatMessage({
                                id: "SelectedFeatureModal.availableIn",
                                defaultMessage: "This update is available in:"
                            })}
                            &nbsp;
                            {selectedFeature.appsCollection.items
                                .map((item) => item.roadmapAppNameExternal)
                                .join(", ")}
                        </div>
                    </div>
                </>
            )}
        </Modal>
    );
};

function getShareableUrl() {
    return window.location.href;
}
